import React, { useState, useMemo } from 'react';
import _ from 'underscore';

import FormModal from 'components/Modals/formModal';
import {
  ApiDescriptionInput,
  ApiModalLabel,
  ApiModalInput,
  ApiTypeSelect,
  ApiParametersRow,
  ApiParameterDeleteContainer,
  DeleteIcon,
  ApiParameterCol,
  ApiParameterChildContainer,
  ApiParameterLabel,
  ErrorMessage,
} from './styles';

//#region helpers

const emptyApi = {
  description: '',
  parameters: [],
  name: '',
  returns: 'None',
};

const typeOptions = { int: 'int', bool: 'bool', float: 'double', str: 'string' };
const collectionTypeOptions = { list: 'list', map: 'map' };
const typeOptionsWithCollection = { '': 'None', ...typeOptions, ...collectionTypeOptions };

const createNewType = (newTypeIndex) => {
  if (newTypeIndex === 5) return { kind: 'list', element: 'int' };
  else if (newTypeIndex === 6) return { kind: 'map', key: 'int', value: 'int' };
  else return _.keys(typeOptionsWithCollection)[newTypeIndex];
};

const validateName = (nameToValidate) => {
  const validNameRegexPattern = /^[a-z][a-z\d]*(\s[a-z\d]+)*$/;
  return validNameRegexPattern.test(nameToValidate);
};

//#endregion helpers

export default function EditApiModal({ apiMethodToEdit, closeModal, onConfirm }) {
  const [apiMethod, setApiMethod] = useState(_.isEmpty(apiMethodToEdit) ? emptyApi : apiMethodToEdit);
  const [newParameterName, setNewParameterName] = useState('');

  const modalTitle = useMemo(() => `${_.isEmpty(apiMethodToEdit) ? 'Add' : 'Edit'} API`, [apiMethodToEdit]);

  const handleNewParameterTypeChange = (newTypeIndex) => {
    let updatedApiMethod = { ...apiMethod };
    updatedApiMethod.parameters.push({ name: newParameterName, type: createNewType(newTypeIndex) });
    setApiMethod(updatedApiMethod);
    setNewParameterName('');
  };

  const handleDeleteParameter = (parameterIndex) => {
    let updatedApiMethod = { ...apiMethod };
    updatedApiMethod.parameters.splice(parameterIndex, 1);
    setApiMethod(updatedApiMethod);
  };

  const handleParameterNameChange = (parameterName, parameterIndex) => {
    let updatedApiMethod = { ...apiMethod };
    updatedApiMethod.parameters[parameterIndex].name = parameterName;
    setApiMethod(updatedApiMethod);
  };

  const handleParameterTypeChange = (newTypeIndex, parameterIndex) => {
    let updatedApiMethod = { ...apiMethod };
    updatedApiMethod.parameters[parameterIndex].type = createNewType(newTypeIndex);
    setApiMethod(updatedApiMethod);
  };

  const handleParameterTypeCollectionTypesChange = (newTypeIndex, typeKey, parent, parameterIndex) => {
    let updatedApiMethod = { ...apiMethod };
    if (parent !== '') updatedApiMethod.parameters[parameterIndex].type[parent][typeKey] = createNewType(newTypeIndex);
    else updatedApiMethod.parameters[parameterIndex].type[typeKey] = createNewType(newTypeIndex);
    setApiMethod(updatedApiMethod);
  };

  const handleReturnTypeChange = (newTypeIndex) => {
    setApiMethod({ ...apiMethod, returns: createNewType(newTypeIndex) });
  };

  const handleReturnTypeCollectionTypesChange = (newTypeIndex, typeKey, parent) => {
    var updatedApiMethod = { ...apiMethod };
    if (parent !== '') updatedApiMethod.returns[parent][typeKey] = createNewType(newTypeIndex);
    else updatedApiMethod.returns[typeKey] = createNewType(newTypeIndex);
    setApiMethod(updatedApiMethod);
  };

  const renderParameters = () => {
    return apiMethod.parameters.map((param, index) => {
      return (
        <React.Fragment key={index}>
          <ApiParametersRow>
            <ApiParameterCol>
              <ApiModalInput
                placeholder="Parameter Name"
                value={param.name}
                onChange={(e) => handleParameterNameChange(e.target.value, index)}
              />
            </ApiParameterCol>
            <ApiParameterCol col="col-5">
              <TypeSelect
                selectedType={param.type}
                onChangeHandler={(newTypeIndex) => handleParameterTypeChange(newTypeIndex, index)}
                allowCollections
              />
            </ApiParameterCol>
            <ApiParameterDeleteContainer>
              <DeleteIcon onClick={() => handleDeleteParameter(index)} />
            </ApiParameterDeleteContainer>
          </ApiParametersRow>
          {param.type?.kind && (
            <ApiParametersRow marginBottom="1.5rem">
              {renderCollectionTypeSelector(param.type, (newTypeIndex, typeKey, parent) =>
                handleParameterTypeCollectionTypesChange(newTypeIndex, typeKey, parent, index)
              )}
            </ApiParametersRow>
          )}
        </React.Fragment>
      );
    });
  };

  const TypeSelect = ({
    selectedType,
    onChangeHandler,
    allowCollections = false,
    allowSelectNone = false,
    disabled = false,
  }) => {
    return (
      <ApiTypeSelect
        disabled={disabled}
        value={typeOptionsWithCollection[selectedType ? (selectedType.kind ? selectedType.kind : selectedType) : '']}
        onChange={(e) => onChangeHandler(allowSelectNone ? e.target.selectedIndex : e.target.selectedIndex + 1)}
      >
        {allowSelectNone && <option key="">None</option>}
        {_.map(typeOptions, (val, key) => {
          return <option key={key}>{val}</option>;
        })}
        {allowCollections &&
          _.map(collectionTypeOptions, (val, key) => {
            return <option key={key}>{val}</option>;
          })}
      </ApiTypeSelect>
    );
  };

  const renderTypeSelector = (type, onChangeHandler) => {
    return (
      <ApiTypeSelect
        value={typeOptionsWithCollection[type ? (type.kind ? type.kind : type) : '']}
        onChange={(e) => onChangeHandler(e.target.selectedIndex)}
      >
        {_.map(typeOptionsWithCollection, (val, key) => {
          return <option key={key}>{val}</option>;
        })}
      </ApiTypeSelect>
    );
  };

  const renderCollectionTypeSelector = (type, onChangeHandler, allowCollections = true, parent = '') => {
    return (
      <>
        {type.kind === 'list' ? (
          <>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Type:</ApiParameterLabel>
              <TypeSelect
                selectedType={type.element}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'element', parent)}
                allowCollections={allowCollections}
              />
              <ApiParameterChildContainer>
                {allowCollections &&
                  type.element.kind &&
                  renderCollectionTypeSelector(type.element, onChangeHandler, false, 'element')}
              </ApiParameterChildContainer>
            </ApiParameterCol>
          </>
        ) : (
          <>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Key:</ApiParameterLabel>
              <TypeSelect
                selectedType={type.key}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'key', parent)}
              />
            </ApiParameterCol>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Value:</ApiParameterLabel>
              <TypeSelect
                selectedType={type.value}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'value', parent)}
                allowCollections={allowCollections}
              />
              <ApiParameterChildContainer>
                {allowCollections &&
                  type.value.kind &&
                  renderCollectionTypeSelector(type.value, onChangeHandler, false, 'value')}
              </ApiParameterChildContainer>
            </ApiParameterCol>
          </>
        )}
      </>
    );
  };

  return (
    <FormModal
      title={modalTitle}
      confirmButtonText="Save"
      onCancel={closeModal}
      onConfirm={() => {
        onConfirm(apiMethod);
        closeModal();
      }}
      //   loading={??}
    >
      All names must be space-separated lists of words containing lowercase letters and numbers, and may not start with
      a number. Specific language formatting will be done automatically.
      <ApiModalInput
        marginTop
        type="text"
        placeholder="Name"
        value={apiMethod.name}
        onChange={(e) => setApiMethod({ ...apiMethod, name: e.target.value })}
      />
      {apiMethod.name !== '' && !validateName(apiMethod.name) && (
        <ErrorMessage>
          Name must be a space-separated list of words containing lowercase letters and numbers, and may not start with
          a number!
        </ErrorMessage>
      )}
      <ApiDescriptionInput
        type="text"
        placeholder=""
        value={apiMethod.description}
        onChange={(e) => setApiMethod({ ...apiMethod, description: e.target.value })}
        row="4"
      />
      <ApiModalLabel>Parameters</ApiModalLabel>
      <ApiParametersRow>
        <ApiParameterCol>
          <ApiModalInput
            value={newParameterName}
            placeholder="Parameter Name"
            onChange={(e) => setNewParameterName(e.target.value)}
          />
        </ApiParameterCol>
        <ApiParameterCol col="col-5">
          <TypeSelect
            type="None"
            disabled={!validateName(newParameterName)}
            onChangeHandler={(selectedIndex) => handleNewParameterTypeChange(selectedIndex)}
            allowSelectNone
            allowCollections
          />
        </ApiParameterCol>
        <ApiParameterDeleteContainer></ApiParameterDeleteContainer>
        {newParameterName !== '' && !validateName(newParameterName) && (
          <ApiParameterCol col="col-12">
            <ErrorMessage>
              Parameter name must be a space-separated list of words containing lowercase letters and numbers, and may
              not start with a number!
            </ErrorMessage>
          </ApiParameterCol>
        )}
      </ApiParametersRow>
      {renderParameters()}
      <ApiModalLabel>Return Type</ApiModalLabel>
      <ApiParametersRow>
        <ApiParameterCol col="col-3">{renderTypeSelector(apiMethod.returns, handleReturnTypeChange)}</ApiParameterCol>
        {apiMethod.returns?.kind &&
          renderCollectionTypeSelector(apiMethod.returns, handleReturnTypeCollectionTypesChange)}
      </ApiParametersRow>
    </FormModal>
  );
}
